import {
  type MasonryEngineDriver,
  type AnyMasonryEngineNode,
  createMasonryEngineActionController,
  HAS_BEEN_TRANSFORMED,
} from '@leagueplatform/masonry-engine';
import type { MasonryDriverAction } from '@leagueplatform/masonry-renderers';
import {
  getAsyncNodeData,
  type MasonryRestGetterParams,
} from '@leagueplatform/masonry-api';
import {
  MasonryDriverNodeRenderersRegistry,
  type MasonryDriverRegistryNode,
} from './masonry-driver-node-renderers-registry';
import {
  DeeplinkActionHandler,
  masonryDriverActionHandlerMap,
} from './masonry-driver-action-handler-map';
import { nodeTransforms } from './node-transforms';

const masonryDriverActionController = createMasonryEngineActionController(
  masonryDriverActionHandlerMap,
);

masonryDriverActionController.registerUniversalHandler(
  'deeplink',
  DeeplinkActionHandler,
  false,
);

const applyNodeTransforms = (
  node: AnyMasonryEngineNode,
  transforms = nodeTransforms,
): AnyMasonryEngineNode => {
  if (node[HAS_BEEN_TRANSFORMED]) {
    return node;
  }

  const transformedNode = transforms.reduce(
    (currentNode, transform) => transform(currentNode),
    node,
  );

  transformedNode[HAS_BEEN_TRANSFORMED] = true;

  Object.keys(transformedNode.sections).forEach((sectionKey) => {
    transformedNode.sections[sectionKey] = transformedNode.sections[
      sectionKey
    ].map((subnode) => applyNodeTransforms(subnode, transforms));
  });

  return transformedNode;
};

export const driver: MasonryEngineDriver<
  MasonryDriverRegistryNode,
  MasonryDriverAction,
  MasonryRestGetterParams
> = {
  nodeRendererRegistry: MasonryDriverNodeRenderersRegistry,
  actionController: masonryDriverActionController,
  getAsyncNodeData: async (getterParams, queryParamsOverride) =>
    applyNodeTransforms(
      (await getAsyncNodeData(
        getterParams,
        queryParamsOverride,
      )) as AnyMasonryEngineNode,
    ),
};
